import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api from "../lib/api";
import { Avatar, Username, VipBadge } from "../components/UserBadge";
import { GAMES } from "../constants/loadout";
import { Crown, Flame, Trophy, MessageSquare, Users, ArrowRight, Zap, Shield, Star, ChevronRight, Activity, Target } from "lucide-react";
import HeroSlider from "../components/HeroSlider";
import Sidebar from "../components/Sidebar";
import ForumCategoryList from "../components/ForumCategoryList";
import { useT } from "../i18n";

export default function Home() {
  const t = useT();
  const { data: boardsData } = useQuery({ queryKey: ["boards"], queryFn: async () => (await api.get("/boards")).data, staleTime: 60000 });
  const { data: vipsData } = useQuery({ queryKey: ["vips"], queryFn: async () => (await api.get("/users/vip?limit=12")).data, staleTime: 60000 });
  const { data: topUsersData } = useQuery({ queryKey: ["topUsers"], queryFn: async () => (await api.get("/users/top?limit=5")).data, staleTime: 60000 });
  const { data: statsData } = useQuery({ queryKey: ["stats"], queryFn: async () => (await api.get("/stats")).data, staleTime: 60000 });
  const { data: monthlyData } = useQuery({ queryKey: ["monthly"], queryFn: async () => (await api.get("/leaderboard/monthly")).data, staleTime: 60000 });
  const { data: settingsData } = useQuery({ queryKey: ["settings"], queryFn: async () => (await api.get("/settings")).data, staleTime: 60000 });

  const boards = boardsData || [];
  const vips = vipsData || [];
  const topUsers = topUsersData || [];
  const stats = statsData || {};
  const monthly = monthlyData || { leaders: [] };
  const settings = settingsData || { slides: [] };

  const features = [
    { icon: MessageSquare, title: "Multi-Game Boards", desc: "CS2, CS 1.6 și CS 1.6 Mix sub un singur forum. Discuții, mix-uri, demo-uri și meta.", color: "from-orange-500/10 to-transparent", border: "hover:border-orange-500/30", iconColor: "text-orange-400" },
    { icon: Crown, title: "Sistem VIP", desc: "Personalizează-ți profilul cu rame animate, GIF-uri, stiluri username și badge-uri exclusive.", color: "from-amber-500/10 to-transparent", border: "hover:border-amber-500/30", iconColor: "text-amber-400" },
    { icon: Trophy, title: "Puncte & Top Lunar", desc: "Postează, primește reacții și câștigă puncte. Liderul lunii primește bonus VIP automat.", color: "from-yellow-500/10 to-transparent", border: "hover:border-yellow-500/30", iconColor: "text-yellow-400" },
    { icon: Users, title: "Comunitate Activă", desc: `${stats.users || "88"}+ operatori înregistrați, ${stats.topics || "87"} topicuri, ${stats.posts || "23"} postări active.`, color: "from-blue-500/10 to-transparent", border: "hover:border-blue-500/30", iconColor: "text-blue-400" },
    { icon: Zap, title: "Reacții Avansate", desc: "5 tipuri de reacții (Like, Fire, Headshot, Ace, Clutch) pentru a-ți exprima reacția.", color: "from-purple-500/10 to-transparent", border: "hover:border-purple-500/30", iconColor: "text-purple-400" },
    { icon: Shield, title: "Panel Admin", desc: "Moderare completă, configurare boarduri, puncte și loadout VIP din admin panel.", color: "from-emerald-500/10 to-transparent", border: "hover:border-emerald-500/30", iconColor: "text-emerald-400" },
  ];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
      <div className="lg:col-span-3 space-y-10">
        <HeroSlider slides={settings.slides || []} />

        {/* ── HERO ─────────────────────────────────── */}
        <section className="relative tf-clip-card overflow-hidden" data-testid="hero-section" style={{ minHeight: 340 }}>
          {/* Background */}
          <div className="absolute inset-0 bg-cover bg-center opacity-25" style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1695812230481-219d762e7eb8)' }} />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0a0c12] via-[#0a0c12]/90 to-[#0a0c12]/60" />
          {/* Scan lines effect */}
          <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(255,255,255,1) 2px, rgba(255,255,255,1) 3px)', pointerEvents: 'none' }} />

          <div className="relative px-10 py-14">
            {/* Top badge */}
            <div className="inline-flex items-center gap-2 mb-5 px-3 py-1.5 bg-[var(--tf-primary)]/10 border border-[var(--tf-primary)]/30 tf-clip-card-sm">
              <span className="w-1.5 h-1.5 bg-[var(--tf-primary)] rounded-full animate-pulse" />
              <span className="text-[8px] sm:text-[10px] font-mono font-bold tracking-[0.15em] sm:tracking-[0.2em] text-[var(--tf-primary)] uppercase">
                EST. 2010 // {stats.users || "98"} OPERATORI · {stats.online_now || "1"} ONLINE
              </span>
            </div>

            <h1 className="font-mono text-3xl sm:text-5xl lg:text-6xl font-black uppercase tracking-tighter leading-none mb-3">
              {t("home.welcome_to") || "Bine ai venit la"}{" "}
              <span className="text-[var(--tf-primary)]">{t("home.title") || "INDUNGI ROMANIA"}</span>
            </h1>
            <p className="text-zinc-300 max-w-xl mb-7 leading-relaxed text-sm">
              {t("home.description") || "Comunitatea românească #1 de Counter-Strike. Forum competitiv pentru CS2, CS1.6 și CS1.6 Mix. Câștigă puncte, urcă în top, deblochează VIP."}
            </p>

            <div className="flex gap-2 sm:gap-3 flex-col sm:flex-row flex-wrap">
              <Link to="/forums" className="tf-btn-primary tf-clip-btn flex items-center justify-center gap-2 w-full sm:w-auto" data-testid="hero-browse-forums">
                {t("home.browse_forums") || "Explorează Forumul"} <ArrowRight className="w-4 h-4" />
              </Link>
              <div className="flex gap-2 sm:gap-3 w-full sm:w-auto">
                <Link to="/vip" className="tf-btn-secondary tf-clip-btn flex flex-1 sm:flex-none items-center justify-center gap-2" data-testid="hero-vip">
                  <Crown className="w-4 h-4" /> {t("home.join_vip") || "Devino VIP"}
                </Link>
                <Link to="/servere" className="tf-btn-secondary tf-clip-btn flex flex-1 sm:flex-none items-center justify-center gap-2">
                  <Target className="w-4 h-4" /> Servere Gaming
                </Link>
              </div>
            </div>

            {/* Inline stat strip */}
            <div className="flex flex-wrap gap-6 mt-8 pt-5 border-t border-white/5">
              {[
                { label: "Membri", value: stats.users || 0, icon: Users, color: "text-blue-400" },
                { label: "Topicuri", value: stats.topics || 0, icon: MessageSquare, color: "text-purple-400" },
                { label: "Postări", value: stats.posts || 0, icon: Activity, color: "text-green-400" },
                { label: "VIP-uri", value: stats.vip_count || 0, icon: Crown, color: "text-amber-400" },
                { label: "Online", value: stats.online_now || 0, icon: Flame, color: "text-[var(--tf-primary)]" },
              ].map(s => (
                <div key={s.label} className="flex items-center gap-2">
                  <s.icon className={`w-4 h-4 ${s.color}`} />
                  <span className="font-mono font-black text-white text-lg">{s.value}</span>
                  <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">{s.label}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── FEATURES ─────────────────────────────── */}
        <section>
          <div className="flex items-end justify-between mb-6">
            <div>
              <h2 className="font-mono text-2xl font-black uppercase tracking-wider text-white">De ce Indungi Romania?</h2>
              <p className="text-xs text-zinc-500 font-mono mt-1">// Tot ce ai nevoie pentru gaming competitiv</p>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {features.map((f) => (
              <div key={f.title} className={`tf-clip-card border border-white/5 ${f.border} bg-gradient-to-br ${f.color} p-5 space-y-3 transition-all group cursor-default`}>
                <div className={`w-9 h-9 tf-clip-card-sm bg-white/5 flex items-center justify-center ${f.iconColor}`}>
                  <f.icon className="w-4.5 h-4.5" />
                </div>
                <div>
                  <h3 className="font-mono font-bold text-white text-sm uppercase tracking-wider mb-1">{f.title}</h3>
                  <p className="text-zinc-500 text-xs leading-relaxed">{f.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* ── FORUM BOARDS ─────────────────────────── */}
        <ForumCategoryList boards={boards} settings={settings} categoriesList={GAMES} />

        {/* ── VIP MEMBERS ──────────────────────────── */}
        <section>
          <div className="flex items-end justify-between mb-5">
            <div>
              <h2 className="font-mono text-2xl font-black uppercase tracking-wider text-white">
                {t("home.join_vip_team") || "Membrii VIP"}
              </h2>
              <p className="text-xs text-zinc-500 font-mono mt-1">{t("home.vip_subtitle") || "// Members who support the community"}</p>
            </div>
            <Link to="/vip" className="flex items-center gap-1.5 text-[var(--tf-primary)] text-xs font-mono hover:underline" data-testid="vip-see-all">
              {t("home.vip_see_all") || "Toți VIP-urile"} <ChevronRight className="w-3.5 h-3.5" />
            </Link>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3" data-testid="vip-grid">
            {vips.map((v) => (
              <Link to={`/u/${v.username}`} key={v.id}
                className="tf-clip-card-sm border border-[var(--tf-primary)]/20 hover:border-[var(--tf-primary)]/50 hover:shadow-lg hover:shadow-[var(--tf-primary)]/10 p-3 flex flex-col items-center text-center transition-all group bg-[#090b10]"
                data-testid={`vip-card-${v.username}`}
              >
                <Avatar user={v} size={40} hover />
                <div className="mt-2 w-full">
                  <Username user={v} className="text-[10px] sm:text-xs truncate w-full text-center" asText />
                </div>
                <div className="scale-90 sm:scale-100 origin-top">
                  <VipBadge tier={v.vip_tier || "veteran"} />
                </div>
              </Link>
            ))}
          </div>
        </section>

        {/* ── LEADERBOARDS ─────────────────────────── */}
        <section className="grid lg:grid-cols-2 gap-5">
          {/* Monthly */}
          <div className="tf-clip-card bg-[#090b10] border border-white/5 p-5 space-y-4" data-testid="monthly-leaderboard">
            <div className="flex items-center justify-between border-b border-white/5 pb-3">
              <div className="flex items-center gap-2">
                <Star className="w-4 h-4 text-[var(--tf-primary)]" />
                <h3 className="font-mono font-bold uppercase tracking-widest text-sm text-white">{t("stats.month_top") || "Top Lunar"}</h3>
              </div>
              <span className="text-[9px] font-mono text-zinc-600">{t("stats.month_top_sub") || "Puncte această lună"}</span>
            </div>
            <div className="space-y-2">
              {(monthly.leaders || []).slice(0, 5).map((u, i) => (
                <Link to={`/u/${u.username}`} key={u.id} className="flex items-center gap-3 px-3 py-2 bg-white/[0.02] hover:bg-white/[0.05] tf-clip-card-sm transition-all">
                  <span className={`font-mono text-lg font-black w-7 ${i === 0 ? 'text-[var(--tf-primary)]' : i === 1 ? 'text-zinc-400' : i === 2 ? 'text-amber-700' : 'text-zinc-600'}`}>#{i + 1}</span>
                  <Avatar user={u} size={32} />
                  <div className="flex-1 min-w-0">
                    <Username user={u} className="text-xs" showBadge />
                  </div>
                  <span className="font-mono text-xs text-zinc-400 font-bold">{u.monthly_points} <span className="text-zinc-600 text-[10px]">pts</span></span>
                </Link>
              ))}
              {!monthly.leaders?.length && <p className="text-sm text-zinc-600 font-mono text-center py-4">{t("stats.no_activity") || "Nicio activitate luna aceasta."}</p>}
            </div>
          </div>

          {/* All-Time */}
          <div className="tf-clip-card bg-[#090b10] border border-white/5 p-5 space-y-4" data-testid="alltime-top">
            <div className="flex items-center justify-between border-b border-white/5 pb-3">
              <div className="flex items-center gap-2">
                <Trophy className="w-4 h-4 text-amber-400" />
                <h3 className="font-mono font-bold uppercase tracking-widest text-sm text-white">{t("home.alltime_frags") || "Top Frags All-Time"}</h3>
              </div>
              <span className="text-[9px] font-mono text-zinc-600">{t("home.alltime_subtitle") || "Cele mai multe puncte"}</span>
            </div>
            <div className="space-y-2">
              {topUsers.map((u, i) => (
                <Link to={`/u/${u.username}`} key={u.id} className="flex items-center gap-3 px-3 py-2 bg-white/[0.02] hover:bg-white/[0.05] tf-clip-card-sm transition-all">
                  <span className={`font-mono text-lg font-black w-7 ${i === 0 ? 'text-[var(--tf-primary)]' : i === 1 ? 'text-zinc-400' : i === 2 ? 'text-amber-700' : 'text-zinc-600'}`}>#{i + 1}</span>
                  <Avatar user={u} size={32} />
                  <div className="flex-1 min-w-0">
                    <Username user={u} className="text-xs" showBadge />
                  </div>
                  <span className="font-mono text-xs text-zinc-400 font-bold">{u.points} <span className="text-zinc-600 text-[10px]">pts</span></span>
                </Link>
              ))}
            </div>
          </div>
        </section>
      </div>

      {/* Sidebar */}
      <div className="lg:col-span-1"><Sidebar /></div>
    </div>
  );
}
